import { useState } from "react";
import { taxEvents } from "../../../mocks/finance";

interface TaxItem {
  id: string;
  title: string;
  dueDate: string;
  amount: number;
  status: string;
  description: string;
  penalty: string;
}

export default function TaxEventPanel() {
  const [taxes, setTaxes] = useState<TaxItem[]>(taxEvents);
  const [paying, setPaying] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ msg: string; type: "success" | "danger" } | null>(null);

  const handlePay = (id: string) => {
    setPaying(id);
    setTimeout(() => {
      setTaxes((prev) => prev.map((t) => t.id === id ? { ...t, status: "납부완료" } : t));
      setPaying(null);
      setNotification({ msg: "납부가 완료되었습니다. 신용도가 유지됩니다.", type: "success" });
      setTimeout(() => setNotification(null), 3000);
    }, 1500);
  };

  const getDaysLeft = (dueDate: string) => {
    const parts = dueDate.split(".");
    const due = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const now = new Date(2026, 2, 27);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getUrgencyStyle = (daysLeft: number, status: string) => {
    if (status === "납부완료") return "border-[#2A2D3E] opacity-60";
    if (daysLeft <= 7) return "border-[#E8375F]/60 bg-[#E8375F]/5";
    if (daysLeft <= 14) return "border-[#FF9F43]/60 bg-[#FF9F43]/5";
    return "border-[#2A2D3E]";
  };

  return (
    <div className="space-y-4">
      {notification && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 border ${
          notification.type === "success"
            ? "bg-[#00C896]/10 text-[#00C896] border-[#00C896]/30"
            : "bg-[#E8375F]/10 text-[#E8375F] border-[#E8375F]/30"
        }`}>
          <i className={`${notification.type === "success" ? "ri-check-double-line" : "ri-alarm-warning-line"} text-lg`}></i>
          {notification.msg}
        </div>
      )}

      <div className="bg-[#1C1F2E] rounded-xl border border-[#E8375F]/30 p-4 flex items-start gap-3">
        <div className="w-6 h-6 flex items-center justify-center mt-0.5 shrink-0">
          <i className="ri-alarm-warning-line text-[#E8375F] text-base"></i>
        </div>
        <div>
          <div className="text-[#111111] text-sm font-semibold mb-1">기한 내 납부 중요!</div>
          <div className="text-[#8B91A8] text-xs leading-relaxed">
            납부 기한을 초과하면 <span className="text-[#E8375F] font-semibold">가산세 + 회사 신용도 포인트 차감</span>이 자동 적용됩니다. 반드시 기한 내에 처리하세요.
          </div>
        </div>
      </div>

      {taxes.map((tax) => {
        const daysLeft = getDaysLeft(tax.dueDate);
        const isDone = tax.status === "납부완료";

        return (
          <div
            key={tax.id}
            className={`bg-[#1C1F2E] rounded-xl border transition-all ${getUrgencyStyle(daysLeft, tax.status)}`}
          >
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[#111111] font-semibold text-sm">{tax.title}</div>
                  <div className="text-[#8B91A8] text-xs mt-1 leading-relaxed">{tax.description}</div>
                </div>
                <div className="text-right shrink-0">
                  {isDone ? (
                    <span className="text-xs text-[#00C896] font-semibold bg-[#00C896]/10 px-2.5 py-1 rounded-full border border-[#00C896]/30">
                      납부완료
                    </span>
                  ) : (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                      daysLeft <= 7 ? "text-[#E8375F] bg-[#E8375F]/10 border-[#E8375F]/30" : "text-[#FF9F43] bg-[#FF9F43]/10 border-[#FF9F43]/30"
                    }`}>
                      D-{daysLeft}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-[#5A5E72] text-[10px]">납부 금액</div>
                    <div className="text-[#111111] font-bold">{tax.amount.toLocaleString()}만원</div>
                  </div>
                  <div>
                    <div className="text-[#5A5E72] text-[10px]">기한</div>
                    <div className="text-[#111111] text-sm">{tax.dueDate}</div>
                  </div>
                </div>
                {!isDone && (
                  <button
                    type="button"
                    onClick={() => handlePay(tax.id)}
                    disabled={paying === tax.id}
                    className="px-4 py-2 bg-gradient-to-r from-[#00C896] to-[#00A87A] text-white text-xs font-semibold rounded-lg hover:shadow-lg hover:shadow-[#00C896]/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {paying === tax.id ? (
                      <><i className="ri-loader-4-line animate-spin mr-1.5"></i>처리중...</>
                    ) : (
                      <><i className="ri-send-plane-line mr-1.5"></i>가상 납부</>
                    )}
                  </button>
                )}
              </div>

              {!isDone && tax.penalty && (
                <div className="mt-3 text-[10px] text-[#E8375F] bg-[#E8375F]/5 rounded-lg px-3 py-2 border border-[#E8375F]/20">
                  <i className="ri-error-warning-line mr-1"></i>{tax.penalty}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
